import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"

import {
  Row,
  Col,
  CardBody,
  Card,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
} from "reactstrap"

//redux
import { useSelector, useDispatch } from "react-redux"
// import { createSelector } from "reselect"
import { Link, useNavigate, useLocation } from "react-router-dom"
import withRouter from "components/Common/withRouter"

// Formik validation
import * as Yup from "utils/yup"
import { useFormik } from "lib/formik"

import AuthBrandingLogo from "components/Authentication/AuthBrandingLogo"
import { loginUser } from "store/actions/authAction"
import { showAlert } from "store/reducers/alertReducer"
import { useTranslation } from "react-i18next"
import Cookies from "js-cookie"
import { resolvePostLoginRedirect } from "utils/accessibleRoutes"
import { ROUTES } from "constants/routes"
import useAuthPageBranding from "hooks/useAuthPageBranding"
import { getApiErrorMessage } from "utils/apiErrorMessage"

const Login = props => {
  const { t } = useTranslation()
  const { brandTitle, authPageClassName } = useAuthPageBranding(t("Sign in"))

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const rememberedEmail = Cookies.get("rememberedEmail")
    const rememberedPassword = Cookies.get("rememberedPassword")
    if (rememberedEmail) {
      validation.setFieldValue("email", rememberedEmail)
      validation.setFieldValue("password", rememberedPassword)
      setRememberMe(true)
    } else {
      setRememberMe(false)
    }
  }, [])

  const handleRememberMeChange = e => {
    setRememberMe(!rememberMe)
    if (!rememberMe) {
      Cookies.remove("rememberedEmail")
      Cookies.remove("rememberedPassword")
    }
  }

  const validation = useFormik({
    // enableReinitialize : use this  flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: values => {
      if (rememberMe) {
        Cookies.set("rememberedEmail", values.email, { expires: 30 })
        Cookies.set("rememberedPassword", values.password, { expires: 30 })
      } else {
        Cookies.remove("rememberedEmail")
        Cookies.remove("rememberedPassword")
      }

      dispatch(
        loginUser({
          ...values,
          history: props.router.navigate,
        })
      )
        .then(action => {
          const payload = action?.payload
          const { statusCode, status, msg, data } = payload || {}
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            if (data?.requires2FA) {
              navigate(ROUTES.LOGIN_OTP, {
                replace: true,
                state: { email: values.email, redirectQuery: location.search },
              })
              return
            }
            // Use validated redirect to prevent open-redirect vulnerabilities
            const redirectTo = resolvePostLoginRedirect(location.search, data)
            navigate(redirectTo, { replace: true })
          } else {
            const { uuid } = data || {}
            dispatch(
              showAlert({
                text:
                  getApiErrorMessage(payload, null, {
                    isAuth: true,
                    fallback: t(
                      "Unable to sign in. Please check your credentials and try again."
                    ),
                  }),
                type: uuid ? "info" : "error",
              })
            )
            if (uuid) {
              navigate(`/reset-password/${uuid}`)
            }
          }
        })
        .catch(error => {
          dispatch(
            showAlert({
              text: getApiErrorMessage(null, error, {
                isAuth: true,
                fallback: t(
                  "Unable to sign in. Please check your credentials and try again."
                ),
              }),
              type: "error",
            })
          )
        })
    },
  })

  const { loading } = useSelector(state => state.Login)

  return (
    <React.Fragment>
      <div className={`account-pages ${authPageClassName}`}>
        <Container
          style={{ display: "flex", alignItems: "center", height: "100%" }}
        >
          <Row className="justify-content-center login_form">
            <Col md={12}>
              <Card className="overflow-hidden">
                <div
                  className="login-platform-hero"
                >
                  <Row>
                    <Col className="col-12">
                      <div
                        className="p-4"
                      >
                        <h5>{brandTitle}</h5>
                        <p>
                          {t("Sign in to continue to your account.")}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <AuthBrandingLogo />
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={e => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      <div className="mb-3">
                        <Label className="form-label">{t("Email")}</Label>
                        <Input
                          name="email"
                          className="form-control"
                          placeholder={t("Email")}
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email && validation.errors.email
                              ? true
                              : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.email}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">{t("Password")}</Label>
                        <div className="position-relative">
                          <Input
                            name="password"
                            value={validation.values.password || ""}
                            type={showPassword ? "text" : "password"}
                            placeholder={t("Password")}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.password &&
                              validation.errors.password
                                ? true
                                : false
                            }
                          />
                          <i
                            onClick={() => setShowPassword(!showPassword)}
                            className={`bx bx-${
                              showPassword ? "show-alt" : "hide"
                            } common_eye`}
                          />
                        </div>
                        {validation.touched.password &&
                        validation.errors.password ? (
                          <FormFeedback type="invalid">
                            {validation.errors.password}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div
                        className="form-check"
                        onClick={handleRememberMeChange}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="customControlInline"
                          checked={rememberMe}
                          // onChange={handleRememberMeChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="customControlInline"
                          onClick={e => {
                            e.preventDefault()
                            handleRememberMeChange(e)
                          }}
                        >
                          {t("Remember me")}
                        </label>
                      </div>

                      <div className="mt-3 d-grid">
                        <button
                          className="btn btn-primary btn-block"
                          type="submit"
                          disabled={loading}
                        >
                          {t("Log In")}
                        </button>
                      </div>

                      <div className="mt-4 text-center">
                        <Link
                          to={ROUTES.FORGOT_PASSWORD}
                          className="text-muted"
                        >
                          <i className="mdi mdi-lock me-1" />
                          {t("Forgot your password?")}
                        </Link>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(Login)

Login.propTypes = {
  history: PropTypes.object,
}
